import { BigNumber } from '@ethersproject/bignumber';
import BURN_REDEEM_721_V2_ABI from '@manifoldxyz/typed-contracts/abis/burnredeem/BURN_REDEEM_721_V2_ABI.json';
import BaseContract from '@/classes/baseContract';
import { TransactionResponse } from '@ethersproject/providers';
import { ValidationTypes } from '@/api/models/burnRedeem';
import { ContractError } from './creatorContract';
import { Contract } from '@ethersproject/contracts';

export type BurnItemOnChain = {
  validationType: ValidationTypes;
  contractAddress: string;
  tokenSpec: number;
  burnSpec: number;
  amount: BigNumber;
  minTokenId?: BigNumber;
  maxTokenId?: BigNumber;
  merkleRoot?: string; // a merkle root hash bytes32
};

export type BurnGroupOnChain = {
  requiredCount: BigNumber; // uint256
  items: BurnItemOnChain[];
};

export type BurnSetOnChain = BurnGroupOnChain[];
/*
 * The data structure returned by the contract
 */
export type BurnRedeemOnChain = {
  paymentReceiver: string;
  storageProtocol: number;
  redeemedCount: number;
  redeemAmount: number;
  totalSupply: number;
  startDate: number; // this is in seconds unix epoch
  endDate: number; // this is in seconds unix epoch
  cost: BigNumber;
  location: string;
  burnSet: BurnSetOnChain;
};

export type BurnToken = {
  groupIndex: number; // uint48
  itemIndex: number; // uint48
  contractAddress: string; // address, creator core
  id: string; // uint256, burn index
  merkleProof: string[]; // bytes32[]
};

export default class BurnRedeemExtensionContractCore extends BaseContract {
  extensionContractAddress: string;
  ABI = BURN_REDEEM_721_V2_ABI; // @dev: default value here is just used for type inferencing, it is overridden in the subclass

  constructor(extensionContractAddress: string) {
    super();
    this.extensionContractAddress = extensionContractAddress;
  }

  async getBurnRedeem(
    redeeemContractAddress: string,
    burnIndex: string,
  ): Promise<BurnRedeemOnChain> {
    return await this._callWeb3WithServerFallback(
      this.extensionContractAddress,
      this.ABI,
      'getBurnRedeem',
      [redeeemContractAddress, burnIndex],
      true,
    );
  }

  getListeningContract(): Contract {
    return this._getContractInstance(this.extensionContractAddress, this.ABI, false, false, true);
  }

  /**
   *
   * @param account                 account that will be used to sign the transaction
   * @param creatorContractAddress  address of the creator contract
   * @param index                   burn index
   * @param burnRedeemCount         number of times the burn redeem will be called
   * @param burnTokens              array of burn tokens to be burned
   * @param fee                     fee to be paid to manifold
   * @param cost                    cost of each redemption
   * @returns
   */
  async burnRedeem(
    account: string,
    creatorContractAddress: string,
    index: string,
    burnRedeemCount: string,
    burnTokens: BurnToken[],
    fee: string,
    cost: string,
  ): Promise<TransactionResponse | undefined> {
    const bigCost = BigNumber.from(cost).mul(burnRedeemCount);
    const bigFee = BigNumber.from(fee);
    const bigTotal = bigFee.add(bigCost);

    try {
      // overestimate gas by 1.25x
      //
      // We have allowlisted calls to `eth_estimateGas` on `safeTransferFrom` signature
      // in manifold provider service. This way, estimation will work in the case
      // of WC provider that cant estimate gas. Otherwise this call  will be made
      // using the browser provider. If all of those fail, we will error.
      const gasEstimate = await this._estimateGas3WithServerFallback(
        this.extensionContractAddress,
        this.ABI,
        'burnRedeem(address,uint256,uint32,(uint48,uint48,address,uint256,bytes32[])[])',
        [
          creatorContractAddress,
          index,
          burnRedeemCount,
          burnTokens,
          {
            from: account,
            value: bigTotal,
          },
        ],
      );

      // On WalletConnect provider, you can only make eth_call calls.
      // This means we have to pass in `unchecked` to ethersjs to
      // sidestep the automatic gas estimation that happens before
      // *all* write calls and then pass in our own gas estimate
      // from before, which in WC's case comes from the manifold
      // provider service.
      let unchecked = false;
      if (
        localStorage.getItem('connectMethod') &&
        localStorage.getItem('connectMethod') === 'walletConnect'
      ) {
        unchecked = true;
      }

      return await this._getContractInstance(
        this.extensionContractAddress,
        this.ABI,
        true,
        false,
        unchecked,
      )['burnRedeem(address,uint256,uint32,(uint48,uint48,address,uint256,bytes32[])[])'](
        creatorContractAddress,
        index,
        burnRedeemCount,
        burnTokens,
        {
          gasLimit: gasEstimate,
          value: bigTotal,
        },
      );
    } catch (e: unknown) {
      await this.errorHandling(e as ContractError);
    }
  }


  /**
   *
   * @param account                   address of the message sender
   * @param creatorContractAddresses  address of the creator contract
   * @param indices                   indices of the burn redeems
   * @param burnRedeemCounts          number of burnRedeems to execute in each redemption (almost always 1)
   * @param burnTokens                tokens to burn with pointers to the corresponding BurnItem/BurnGroup requirement
   * @param fee                       fee to pay manifold
   * @param cost                      cost of each mint/redemption
   * @returns
   */
  async burnRedeemOverload(
    account: string,
    creatorContractAddresses: string[],
    indices: string[],
    burnRedeemCounts: string[],
    burnTokens: BurnToken[][],
    fee: string,
    cost: string,
  ): Promise<TransactionResponse | undefined> {
    const bigCost = BigNumber.from(cost).mul(
      burnRedeemCounts.reduce((a, b) => (a + parseInt(b)), 0),
    );
    const bigFee = BigNumber.from(fee);
    const bigTotal = bigFee.add(bigCost);

    try {
      // overestimate gas by 1.25x
      //
      // We have allowlisted calls to `eth_estimateGas` on `safeTransferFrom` signature
      // in manifold provider service. This way, estimation will work in the case
      // of WC provider that cant estimate gas. Otherwise this call  will be made
      // using the browser provider. If all of those fail, we will error.
      const gasEstimate = await this._estimateGas3WithServerFallback(
        this.extensionContractAddress,
        this.ABI,
        'burnRedeem(address[],uint256[],uint32[],(uint48,uint48,address,uint256,bytes32[])[][])',
        [
          creatorContractAddresses,
          indices,
          burnRedeemCounts,
          burnTokens,
          {
            from: account,
            value: bigTotal,
          },
        ],
      );

      // On WalletConnect provider, you can only make eth_call calls.
      // This means we have to pass in `unchecked` to ethersjs to
      // sidestep the automatic gas estimation that happens before
      // *all* write calls and then pass in our own gas estimate
      // from before, which in WC's case comes from the manifold
      // provider service.
      let unchecked = false;
      if (
        localStorage.getItem('connectMethod') &&
        localStorage.getItem('connectMethod') === 'walletConnect'
      ) {
        unchecked = true;
      }

      return await this._getContractInstance(
        this.extensionContractAddress,
        this.ABI,
        true,
        false,
        unchecked,
      )['burnRedeem(address[],uint256[],uint32[],(uint48,uint48,address,uint256,bytes32[])[][])'](
        creatorContractAddresses,
        indices,
        burnRedeemCounts,
        burnTokens,
        {
          gasLimit: gasEstimate,
          value: bigTotal,
        },
      );
    } catch (e: unknown) {
      await this.errorHandling(e as ContractError);
    }
  }

  async errorHandling(error: ContractError): Promise<void> {
    if (error.code === 'TRANSACTION_REPLACED' && !error.cancelled && error.replacement) {
      const provider = window.ManifoldEthereumProvider.provider();
      if (!provider) {
        throw new Error('No web3 provider detected, please refresh the page and try again');
      }
      const tx = await provider.getTransaction(error.replacement.hash);
      // Possible failed transaction
      if (!tx) {
        throw new Error('Failed Transaction');
      }
      await tx.wait(1);
    } else {
      throw error;
    }
  }
}
